import { useState, useMemo } from 'react';
import { toast } from '@/components/ui/use-toast';
import { inviteStaff } from '@/services/global/admin.service';
import { useManageStaffStore } from '../store/useManageStaffStore';
import { useAuthentication } from '@/hooks/useAuthentication';

type UseInviteStaffParams = {
  refetch?: () => void;
  rowsPerPage?: number;
};

export type RowData = {
  surname: string;
  forename: string;
  email: string;
};

export const useInviteStaff = ({ refetch, rowsPerPage = 10 }: UseInviteStaffParams = {}) => {
  const inviteStaffData = useManageStaffStore((state) => state?.page?.inviteStaff);
  const { schoolContext } = useAuthentication();

  const [isOpenInviteDialog, setIsOpenInviteDialog] = useState(false);
  const [usersPreview, setUsersPreview] = useState<RowData[]>([]);
  const [loadingInvite, setLoadingInvite] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [loadingSendInvitation, setLoadingSendInvitation] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(usersPreview.length / rowsPerPage)),
    [usersPreview.length, rowsPerPage],
  );

  const paginatedData = useMemo(
    () => usersPreview.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage),
    [usersPreview, currentPage, rowsPerPage],
  );

  const handlePageChange = (page: number) => setCurrentPage(page);

  const onShowInvitationPreview = async () => {
    setLoadingInvite(true);
    const { data, error } = await inviteStaff(schoolContext?.documentId || '', true);
    if (error) {
      toast({
        title: inviteStaffData?.toastStaffInvitedTitle || '',
        description: error.message,
        variant: 'destructive',
      });
      setLoadingInvite(false);
      return;
    }
    setUsersPreview(data?.importedUsers || []);
    setCurrentPage(1);
    setIsOpenInviteDialog(true);
    setLoadingInvite(false);
  };

  const onSendInvitationEmail = async () => {
    setLoadingSendInvitation(true);
    const { data, error } = await inviteStaff(schoolContext?.documentId || '', false);

    if (error) {
      toast({
        title: inviteStaffData?.toastStaffInvitedTitle || '',
        description: error.message,
        variant: 'destructive',
      });
      setLoadingSendInvitation(false);
      return;
    }

    toast({
      title: inviteStaffData?.toastStaffInvitedTitle || '',
      description: `${data.size} ${inviteStaffData?.toastStaffInvitedDescription || ''}`,
    });

    await refetch?.();
    setLoadingSendInvitation(false);
    setIsOpenInviteDialog(false);
  };

  return {
    loadingInvite,
    onShowInvitationPreview,
    isOpenInviteDialog,
    setIsOpenInviteDialog,
    paginatedData,
    totalPages,
    currentPage,
    handlePageChange,
    onSendInvitationEmail,
    loadingSendInvitation,
  };
};
